/**
 * Joins the base tag of a customer form with the chosen interests, as Shopify expects in `contact[tags]`.
 *
 * @param {string} base - Base tag(s), e.g. `'newsletter'`.
 * @param {string[]} interests - Checked interests, e.g. `['Nouveautés', 'Promotions']`.
 * @returns {string} e.g. `'newsletter, Nouveautés, Promotions'`.
 */
export function mergeTags(base, interests) {
  const tags = [base, ...interests].map((tag) => tag.replaceAll(',', ' ').trim()).filter(Boolean);
  return [...new Set(tags)].join(', ');
}

/**
 * `<newsletter-form>` around the footer's `{% form 'customer' %}`: on submit, merges the checked interest boxes
 * (`[data-nl-interest]`, which have no `name`, so without JS only the base tag is sent) into the hidden
 * `[data-nl-tags]` input (`contact[tags]`, base tag in its `value`).
 */
export class NewsletterForm extends HTMLElement {
  // Listen on the host (submit bubbles): children may not be parsed yet when connectedCallback runs.
  connectedCallback() {
    this.addEventListener('submit', this.onSubmit);
  }

  disconnectedCallback() {
    this.removeEventListener('submit', this.onSubmit);
  }

  /** @param {SubmitEvent} event */
  onSubmit = (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;
    const tags = form.querySelector('input[data-nl-tags]');
    if (!(tags instanceof HTMLInputElement)) return;
    tags.dataset.base ??= tags.value;
    const interests = /** @type {HTMLInputElement[]} */ ([...form.querySelectorAll('input[data-nl-interest]')])
      .filter((input) => input.checked)
      .map((input) => input.value);
    tags.value = mergeTags(tags.dataset.base, interests);
  };
}

if (!customElements.get('newsletter-form')) customElements.define('newsletter-form', NewsletterForm);
